import { useEffect, useState } from 'react';
import { Check } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RemindingTone, useUpdateUserMutation, useUserQuery } from '../../api/gql';
import { FullSizeLoader } from '../../components/FullSizeLoader';
import { RemindingTones } from '../../components/RemindingTones';
import { BareLayout, PageContent } from '../../layout';
import { notify } from '../../utils/notification';
import { useSafeNavigation } from '../../hooks/useSafeNavigation';

export const RegistrationRemindingTonePage = () => {
  const { t } = useTranslation();
  const [selectedRemindingTone, setSelectedRemindingTone] = useState<RemindingTone>(RemindingTone.Neutral);

  const { safeNavigate } = useSafeNavigation();

  const { data, isLoading } = useUserQuery(
    {},
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    const defaultRemindingTone = data.user.defaultRemindingTone;
    setSelectedRemindingTone(defaultRemindingTone);
  }, [data]);

  const { mutate, isPending: isMutating } = useUpdateUserMutation({
    onSuccess: () => {
      notify.positive({ message: t('remindingTone.change.success') });
    },
    onError: (error) => {
      notify.negative({ message: t('remindingTone.change.error') });
      console.error(error);
    },
    onSettled: () => {
      safeNavigate('/');
    },
  });

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRemindingTone) {
      return;
    }

    mutate({
      input: {
        defaultRemindingTone: selectedRemindingTone,
      },
    });
  };

  return (
    <BareLayout>
      <Typography variant={'h1'} textAlign={'center'}>
        {t('remindingTone.title')}
      </Typography>
      <Box pt={4} display={'flex'} justifyContent={'center'}>
        <Alert severity={'info'} sx={{ whiteSpace: 'break-spaces' }}>
          {t('registrationRemindingTone.subtitle')}
        </Alert>
      </Box>
      <PageContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          pt: 8,
        }}
      >
        {isLoading ? <FullSizeLoader /> : null}
        <Typography variant={'h2'} color={'text.secondary'} sx={{ pb: 8 }}>
          {t('prefillAutomatically')}
        </Typography>
        <form onSubmit={onSubmit}>
          <RemindingTones onToneChange={setSelectedRemindingTone} selected={selectedRemindingTone} />
          <Box display={'flex'} justifyContent={'center'} mt={8}>
            <LoadingButton
              startIcon={<Check />}
              loading={isMutating}
              type={'submit'}
              size={'large'}
              variant={'contained'}
            >
              {t('saveSettings')}
            </LoadingButton>
          </Box>
        </form>
      </PageContent>
    </BareLayout>
  );
};
